import React, { useContext, useState } from "react";
import {
  Avatar,
  Badge,
  IconButton,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Box
} from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import { makeStyles } from "@material-ui/core/styles";
import { AccountCircle } from "@material-ui/icons";
import NotificationsIcon from "@material-ui/icons/Notifications";
import logo from "../../../assets/logo.svg";
import UserContext from "../../../UserContext";
import useNotification from "../../../hooks/notification/useNotification";
import { notificationHubUrl } from "../../../consts/apiUrls";
import { getAuthorizationToken } from "../../../utilities/tokenUtilities";
import { hrDateWithTime } from "../../../utilities/utilities";

const useStyles = makeStyles(theme => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  toolbar: {
    paddingRight: 24
  },
  title: {
    flexGrow: 1,
    marginLeft: "5px"
  },
  logoContainer: {
    borderRadius: 0,
    marginLeft: "-15px"
  },
  logo: {
    width: "75%"
  },
  menuContainer: {
    border: "1px solid #e0e0e0;"
  },
  accountCircle: {
    ...theme.typography.button,
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    display: "inline-flex"
  },
  menuItem: {
    cursor: "default"
  }
}));

export default function Header() {
  const classes = useStyles({});
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const { notifications, markNotificationsAsReaded } = useNotification({
    hubUrl: notificationHubUrl(),
    accessToken: getAuthorizationToken() || ""
  });
  const { user } = useContext(UserContext);
  const isNotificationsMenuOpen = Boolean(anchorElement);

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (notifications.length > 0) {
      setAnchorElement(event.currentTarget);
    }
  };

  const handleNotificationMenuClose = () => {
    setAnchorElement(null);
    markNotificationsAsReaded();
  };

  const renderNotificationMenu = (
    <Menu
      elevation={0}
      getContentAnchorEl={null}
      anchorEl={anchorElement}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center"
      }}
      keepMounted
      classes={{ paper: classes.menuContainer }}
      open={isNotificationsMenuOpen}
      onClose={handleNotificationMenuClose}
    >
      <div>
        {notifications.map((n, i) => (
          <MenuItem key={i} className={classes.menuItem}>
            <Box
              fontSize="body2.fontSize"
              fontWeight={n.isReaded ? "fontWeightRegular" : "fontWeightBold"}
            >
              <Box fontSize="caption.fontSize">{hrDateWithTime(n.date)}</Box>
              {n.message}
            </Box>
          </MenuItem>
        ))}
      </div>
    </Menu>
  );

  const getUnreadNotificationsCount = () => {
    return notifications.filter(n => !n.isReaded).length;
  };

  return (
    <AppBar position="absolute" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <Avatar
          alt="logo"
          src={logo}
          className={classes.logoContainer}
          classes={{ img: classes.logo }}
        />
        <Typography
          variant="body2"
          color="inherit"
          noWrap
          className={classes.title}
        >
          MINISTARSTVO ZNANOSTI, OBRAZOVANJA I MLADIH<br />
          REPUBLIKE HRVATSKE
        </Typography>
        <IconButton color="inherit" onClick={handleNotificationMenuOpen}>
          <Badge badgeContent={getUnreadNotificationsCount()} color="secondary">
            <NotificationsIcon />
          </Badge>
        </IconButton>

        <div color="inherit" className={classes.accountCircle}>
          <AccountCircle />
        </div>
        {user.firstName + " " + user.lastName}
      </Toolbar>
      {renderNotificationMenu}
    </AppBar>
  );
}
